<template>
  <ClientOnly>
    <div 
      class="bubble-container" 
      :class="{ 'is-hidden': shouldHideInMobile }"
      @mouseenter="handleFirstHover"
    >
    <!-- Inbox/Feedback Button -->
    <button
      class="bubble-button inbox-button"
      :class="{ 'is-active': activeState === 'inbox' }"
      :aria-label="activeState === 'inbox' ? 'Close inbox' : 'Open inbox'"
      :aria-pressed="activeState === 'inbox'"
      @click="handleInboxClick"
    >
      <Icon name="lucide:mail" />
    </button>

    <!-- AI Chat Button -->
    <button
      class="bubble-button ai-button"
      :class="{ 'is-active': activeState === 'ai-chat' }"
      :aria-label="activeState === 'ai-chat' ? 'Close AI chat' : 'Open AI chat'"
      :aria-pressed="activeState === 'ai-chat'"
      @click="handleAiChatClick"
    >
      <Icon name="lucide:sparkles" />
    </button>
    </div>
  </ClientOnly>
</template>

<script setup>
import { useBreakpoints, breakpointsTailwind } from "@vueuse/core";
import { computed, ref } from "#imports";
import { useDebugLogger } from "../../composables/useDebugLogger";
const props = defineProps({
  activeState: { type: String, required: false, default: "closed" }
});
const emit = defineEmits(["stateChange", "firstHover"]);
const { log } = useDebugLogger();
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("md");
const hasHovered = ref(false);
const isDrawerOpen = computed(() => {
  return props.activeState !== "closed";
});
const shouldHideInMobile = computed(() => {
  return isMobile.value && isDrawerOpen.value;
});
function handleFirstHover() {
  if (!hasHovered.value) {
    hasHovered.value = true;
    emit("firstHover");
    log("\u{1F3AF} First hover detected - loading drawer components");
  }
}
function handleInboxClick() {
  const newState = props.activeState === "inbox" ? "closed" : "inbox";
  emit("stateChange", newState);
}
function handleAiChatClick() {
  const newState = props.activeState === "ai-chat" ? "closed" : "ai-chat";
  emit("stateChange", newState);
}
</script>

<style scoped>
.bubble-container{--color-primary:#6366f1;--active-start:#6366f1;--active-end:#8b5cf6;--active-text-color:#fff;--hover-start:#c1c0de;--hover-end:#bfabe1;--text-color:var(--color-primary);--shadow-color:99,102,241;--bubble-bg:hsla(0,0%,100%,.95);--focus-ring-color:rgba(99,102,241,.3);--inbox-color:#fff;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);background:var(--bubble-bg);border:1px solid hsla(0,0%,100%,.2);border-radius:40px;bottom:32px;box-shadow:0 8px 32px rgba(var(--shadow-color),.2);display:flex;gap:8px;padding:4px;position:fixed;right:72px;transition:opacity .3s cubic-bezier(.4,0,.2,1),visibility .3s cubic-bezier(.4,0,.2,1);z-index:1000}.bubble-container.is-hidden{opacity:0;pointer-events:none;visibility:hidden}.bubble-button{align-items:center;border:none;border-radius:32px;cursor:pointer;display:flex;font-size:16px;height:48px;justify-content:center;padding:12px;transition:all .3s cubic-bezier(.4,0,.2,1);width:48px}.bubble-button:hover{transform:translateY(-1px)}.bubble-button:active{color:var(--active-text-color);transform:translateY(0)}.bubble-button:focus{box-shadow:0 0 0 3px var(--focus-ring-color);outline:none}.ai-button,.inbox-button{background:var(--inbox-color);box-shadow:0 4px 16px rgba(var(--shadow-color),.3);color:var(--text-color)}.ai-button:hover,.inbox-button:hover{background:linear-gradient(135deg,var(--hover-start) 0,var(--hover-end) 100%);box-shadow:0 6px 20px rgba(var(--shadow-color),.4)}.ai-button:active,.bubble-button.is-active,.inbox-button:active{background:linear-gradient(135deg,var(--active-start) 0,var(--active-end) 100%)}.bubble-button.is-active{box-shadow:0 6px 20px rgba(var(--shadow-color),.5);color:var(--active-text-color);transform:translateY(0)}.bubble-button.is-active:hover{background:linear-gradient(135deg,var(--active-start) 0,var(--active-end) 100%);transform:translateY(-1px)}@media (max-width:768px){.bubble-container{bottom:24px;right:24px}}
</style>
